import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 12; // ~24 seconds

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clearCart } = useCart();

  // 'loading' | 'paid' | 'unpaid' | 'expired' | 'error' | 'missing'
  const [state, setState] = useState(sessionId ? 'loading' : 'missing');
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const clearedRef = useRef(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);

        if (data.payment_status === 'paid') {
          setState('paid');
          if (!clearedRef.current) {
            clearCart();
            clearedRef.current = true;
          }
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState('unpaid');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          'Could not verify payment status.';
        setErrorMsg(typeof msg === 'string' ? msg : 'Unknown error');
        setState('error');
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-32">
      <div className="max-w-xl w-full text-center">
        {state === 'loading' && (
          <Pending title="Verifying your payment…" body="This usually takes a few seconds." />
        )}

        {state === 'paid' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-neutral-900 text-white flex items-center justify-center mb-8">
              <Check className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5">Payment Confirmed</p>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mb-6">
              Thank you for your order.
            </h1>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We have received your payment of{' '}
              <span className="text-neutral-900 font-medium">
                {info ? formatCurrency(info.amount_total, info.currency) : ''}
              </span>
              .
            </p>
            <p className="text-neutral-600 leading-relaxed mb-8">
              A confirmation will be sent to{' '}
              <span className="text-neutral-900">
                {info?.metadata?.customer_email || 'your email'}
              </span>
              . Your reference is{' '}
              <span className="text-neutral-900 font-medium">
                {sessionId ? sessionId.slice(-10).toUpperCase() : ''}
              </span>
              .
            </p>
            <Link
              to="/shop"
              className="inline-block bg-neutral-900 text-white px-12 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
            >
              Continue Shopping
            </Link>
          </>
        )}

        {state === 'unpaid' && (
          <Issue
            title="Payment is still processing."
            body="Your payment hasn't confirmed yet. We'll send you a confirmation email as soon as it does. You can refresh this page to check again."
            action="Back to Shop"
          />
        )}

        {state === 'expired' && (
          <Issue
            title="Your payment session expired."
            body="No charge was made. Please return to checkout and try again."
            action="Back to Checkout"
            actionTo="/checkout"
          />
        )}

        {state === 'error' && (
          <Issue
            title="We couldn't verify your payment."
            body={errorMsg || 'Please contact our client care team if you were charged.'}
            action="Back to Checkout"
            actionTo="/checkout"
          />
        )}

        {state === 'missing' && (
          <Issue
            title="No payment to verify."
            body="It looks like you arrived here directly. Start a new order from your bag."
            action="Back to Shop"
          />
        )}
      </div>
    </div>
  );
};

const Pending = ({ title, body }) => (
  <>
    <div className="w-14 h-14 mx-auto rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center mb-8">
      <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.5} />
    </div>
    <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5">One Moment</p>
    <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mb-6">{title}</h1>
    <p className="text-neutral-600 leading-relaxed">{body}</p>
  </>
);

const Issue = ({ title, body, action = 'Back to Shop', actionTo = '/shop' }) => (
  <>
    <div className="w-14 h-14 mx-auto rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center mb-8">
      <AlertCircle className="w-6 h-6" strokeWidth={1.5} />
    </div>
    <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5">Status</p>
    <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mb-6">{title}</h1>
    <p className="text-neutral-600 leading-relaxed mb-8">{body}</p>
    <Link
      to={actionTo}
      className="inline-block bg-neutral-900 text-white px-12 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
    >
      {action}
    </Link>
  </>
);

const formatCurrency = (cents, currency) => {
  if (typeof cents !== 'number') return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

export default CheckoutSuccess;
